import { fetchJSON as fetchAPI } from './client';

export type PromotionOpsDashboard = {
  tenant_id: string;
  active_campaigns: number;
  scheduled_campaigns: number;
  upcoming_events: number;
  total_budget: number;
  budget_utilization_pct: number;
  avg_roi: number;
  avg_margin_impact_pct: number;
  avg_event_readiness_pct: number;
  active_recommendations: number;
  generated_at?: string;
};

export type CampaignRecord = {
  id: string;
  name: string;
  status: string;
  event_id?: string;
  promotion_type?: string;
  budget: number;
  spent: number;
  product_count: number;
  requires_approval: boolean;
  start_date?: string;
  end_date?: string;
};

export type PromotionOfferRecord = {
  id: string;
  campaign_id?: string;
  asin?: string;
  sku?: string;
  promotion_type: string;
  discount_pct?: number;
  status: string;
  eligibility?: string;
};

export type EventRecord = {
  id: string;
  name: string;
  type?: string;
  status: string;
  marketplace?: string;
  start_date: string;
  end_date: string;
  days_until: number;
  readiness_pct: number;
};

export type BudgetRecord = {
  id: string;
  campaign_id?: string;
  category?: string;
  allocated: number;
  spent: number;
  remaining: number;
  utilization_pct: number;
};

export type ForecastRecord = {
  id: string;
  campaign_id?: string;
  event_id?: string;
  status: string;
  expected_demand: number;
  expected_revenue: number;
  expected_profit: number;
  required_inventory: number;
  advertising_budget: number;
  risk_score: number;
};

export type ScenarioRecord = {
  id: string;
  name: string;
  description?: string;
  scenario_type?: string;
  projected_revenue: number;
  projected_profit: number;
  projected_roi: number;
  margin_impact_pct: number;
  break_even_days: number;
};

export type PerformanceRecord = {
  id: string;
  campaign_id?: string;
  campaign_name?: string;
  incremental_revenue: number;
  incremental_profit: number;
  roi: number;
  margin_impact_pct: number;
  units_sold: number;
  conversion_rate: number;
};

export type PromotionRecommendation = {
  id: string;
  type: string;
  title: string;
  description?: string;
  campaign_id?: string;
  event_id?: string;
  sku?: string;
  expected_impact?: string;
  confidence_score: number;
  priority_rank: number;
  requires_approval: boolean;
  source_module?: string;
};

const API_BASE = import.meta.env.VITE_PROMOTIONOPS_API ?? '/api/v1/promotionops';

function fetchJSON<T>(path: string, init?: RequestInit): Promise<T> {
  return fetchAPI<T>(API_BASE, path, init);
}

export const promotionopsApi = {
  dashboard: () => fetchJSON<PromotionOpsDashboard>('/dashboard'),
  listCampaigns: () => fetchJSON<CampaignRecord[]>('/campaigns'),
  listPromotions: () => fetchJSON<PromotionOfferRecord[]>('/promotions'),
  listEvents: () => fetchJSON<EventRecord[]>('/events'),
  listBudgets: () => fetchJSON<BudgetRecord[]>('/budgets'),
  listForecasts: () => fetchJSON<ForecastRecord[]>('/forecasts'),
  listScenarios: () => fetchJSON<ScenarioRecord[]>('/scenarios'),
  listPerformance: () => fetchJSON<PerformanceRecord[]>('/performance'),
  listRecommendations: () => fetchJSON<PromotionRecommendation[]>('/recommendations'),
  generateRecommendations: () =>
    fetchJSON<PromotionRecommendation[]>('/recommendations/generate', { method: 'POST' }),
  listSyncJobs: () => fetchJSON<{ id: string; mode: string; status: string }[]>('/sync-jobs'),
  listActivity: () => fetchJSON<{ id: string; message: string }[]>('/activity'),
};
